import React from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Card, Descriptions, Tag, Table, Spin, Empty } from 'antd'
import { fetchCustomerDetail } from '@/redux/order/customerSlice'
import { renderTextStr, renderPhoneStr, formatDateTimeStr } from '@/utils/renderUtils'

// 客户标签配置
const TAG_CONFIG = {
  VIP: { text: 'VIP', color: 'red' },
  新: { text: '新客', color: 'blue' },
  旧: { text: '旧客', color: 'green' }
}

// 渠道配置
const CHANNEL_CONFIG = {
  xiaohongshu: { text: '小红书', color: 'red' },
  douyin: { text: '抖音', color: 'blue' },
  weixin: { text: '微信', color: 'green' },
  other: { text: '其他', color: 'default' }
}

// 订单状态配置
const ORDER_STATUS_CONFIG = {
  abnormal: { text: '异常订单', color: 'red' },
  normal: { text: '正常订单', color: 'green' },
  overtime: { text: '超时订单', color: 'orange' },
  pending: { text: '待支付', color: 'default' },
  booked: { text: '已预约', color: 'blue' },
  in_progress: { text: '进行中', color: 'processing' },
  completed: { text: '已完成', color: 'success' }
}

// 服务类型配置
const SERVICE_TYPE_CONFIG = {
  maternity: { text: '产康', color: 'purple' },
  nursing: { text: '护理', color: 'cyan' },
  home: { text: '到家', color: 'geekblue' }
}

class CustomerDetailContent extends React.Component {
  componentDidMount() {
    const { customerId, dispatch } = this.props
    if (customerId && dispatch) {
      dispatch(fetchCustomerDetail(customerId))
    }
  }

  componentDidUpdate(prevProps) {
    const { customerId, dispatch } = this.props
    if (customerId && customerId !== prevProps.customerId && dispatch) {
      dispatch(fetchCustomerDetail(customerId))
    }
  }

  render() {
    const { customerDetailObj } = this.props
    const { fetching, data: customerDetail } = customerDetailObj || {}

    if (fetching) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      )
    }

    if (!customerDetail) {
      return (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Empty description="客户不存在或加载失败" />
        </div>
      )
    }

    const channelConfig = CHANNEL_CONFIG[customerDetail.channel] || {}

    return (
      <div>
        {/* 客户详细资料 */}
        <Card title="客户详细资料" style={{ marginBottom: '16px' }}>
          <Descriptions bordered column={2} size="small">
            <Descriptions.Item label="客户姓名">
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{renderTextStr(customerDetail.name)}</span>
                {customerDetail.tags && customerDetail.tags.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    {customerDetail.tags.map((tag, index) => {
                      const config = TAG_CONFIG[tag] || { text: tag, color: 'default' }
                      return (
                        <Tag key={index} color={config.color}>
                          {config.text}
                        </Tag>
                      )
                    })}
                  </div>
                )}
              </div>
            </Descriptions.Item>
            <Descriptions.Item label="手机号">
              {renderPhoneStr(customerDetail.phone)}
            </Descriptions.Item>
            <Descriptions.Item label="地址" span={2}>
              {renderTextStr(customerDetail.address)}
            </Descriptions.Item>
            <Descriptions.Item label="经纬度">
              {customerDetail.points && customerDetail.points.length >= 2
                ? `${customerDetail.points[0]}, ${customerDetail.points[1]}`
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="渠道">
              {customerDetail.channel ? (
                <Tag color={channelConfig.color}>{channelConfig.text}</Tag>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="门店">
              {renderTextStr(customerDetail.storeName)}
            </Descriptions.Item>
            <Descriptions.Item label="录入人员">
              {renderTextStr(customerDetail.createdBy)}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {formatDateTimeStr(customerDetail.createTime)}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {formatDateTimeStr(customerDetail.updateTime)}
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* 服务历史 */}
        <Card title="服务历史" style={{ marginBottom: '16px' }}>
          {customerDetail.orderHistory && customerDetail.orderHistory.length > 0 ? (
            <Table
              dataSource={customerDetail.orderHistory}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: '订单信息',
                  key: 'orderInfo',
                  width: 200,
                  render: (_, record) => {
                    const orderNo = renderTextStr(record.orderNo)
                    const createTime = formatDateTimeStr(record.createTime)
                    return (
                      <div>
                        <div style={{ marginBottom: '4px' }}>{orderNo}</div>
                        <div style={{ color: '#999', fontSize: '12px' }}>{createTime}</div>
                      </div>
                    )
                  }
                },
                {
                  title: '服务类型',
                  dataIndex: 'serviceType',
                  key: 'serviceType',
                  width: 100,
                  render: (serviceType) => {
                    const config = SERVICE_TYPE_CONFIG[serviceType] || {}
                    return <Tag color={config.color}>{config.text || serviceType}</Tag>
                  }
                },
                {
                  title: '订单状态',
                  dataIndex: 'status',
                  key: 'status',
                  width: 120,
                  render: (status) => {
                    const config = ORDER_STATUS_CONFIG[status] || {}
                    return <Tag color={config.color}>{config.text || status}</Tag>
                  }
                },
                {
                  title: '订单金额',
                  dataIndex: 'amount',
                  key: 'amount',
                  width: 120,
                  align: 'right',
                  render: (amount) => amount ? `¥${Number(amount).toFixed(2)}` : '-'
                },
                {
                  title: '服务时间',
                  key: 'serviceTime',
                  width: 200,
                  render: (_, record) => {
                    const startTime = record.startTime ? formatDateTimeStr(record.startTime) : '-'
                    const endTime = record.endTime ? formatDateTimeStr(record.endTime) : '-'
                    return (
                      <div>
                        <div>开始：{startTime}</div>
                        <div style={{ color: '#999', fontSize: '12px' }}>结束：{endTime}</div>
                      </div>
                    )
                  }
                },
              ]}
            />
          ) : (
            <Empty description="暂无服务历史记录" />
          )}
        </Card>

        {/* 客户偏好记录 */}
        <Card title="客户偏好记录" style={{ marginBottom: '16px' }}>
          {customerDetail.preferences && Object.keys(customerDetail.preferences).length > 0 ? (
            <Descriptions bordered column={1} size="small">
              {customerDetail.preferences.favoriteFood && (
                <Descriptions.Item label="喜欢吃什么">
                  {customerDetail.preferences.favoriteFood}
                </Descriptions.Item>
              )}
              {customerDetail.preferences.personality && (
                <Descriptions.Item label="个人性格">
                  {customerDetail.preferences.personality}
                </Descriptions.Item>
              )}
              {customerDetail.preferences.hobbies && (
                <Descriptions.Item label="独特爱好">
                  {customerDetail.preferences.hobbies}
                </Descriptions.Item>
              )}
              {/* 兼容其他可能的偏好字段 */}
              {Object.entries(customerDetail.preferences)
                .filter(([key]) => !['favoriteFood', 'personality', 'hobbies'].includes(key))
                .map(([key, value]) => (
                  <Descriptions.Item key={key} label={key}>
                    {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                  </Descriptions.Item>
                ))}
            </Descriptions>
          ) : (
            <Empty description="暂无客户偏好记录" />
          )}
        </Card>

        {/* 相关跟进人资料 */}
        <Card title="相关跟进人资料">
          {customerDetail.followUpStaff && customerDetail.followUpStaff.length > 0 ? (
            <Table
              dataSource={customerDetail.followUpStaff}
              rowKey="id"
              pagination={false}
              size="small"
              columns={[
                {
                  title: '姓名',
                  dataIndex: 'name',
                  key: 'name',
                  width: 120,
                  render: (text) => renderTextStr(text)
                },
                {
                  title: '电话',
                  dataIndex: 'phone',
                  key: 'phone',
                  width: 130,
                  render: (text) => renderPhoneStr(text)
                },
                {
                  title: '职位',
                  dataIndex: 'position',
                  key: 'position',
                  width: 120,
                  render: (text) => renderTextStr(text)
                },
                {
                  title: '部门',
                  dataIndex: 'department',
                  key: 'department',
                  width: 150,
                  render: (text) => renderTextStr(text)
                },
                {
                  title: '备注',
                  dataIndex: 'remark',
                  key: 'remark',
                  render: (text) => renderTextStr(text)
                }
              ]}
            />
          ) : (
            <Empty description="暂无相关跟进人资料" />
          )}
        </Card>
      </div>
    )
  }
}

// 使用函数组件作为wrapper来使用React Redux Hooks
const CustomerDetailContentWrapper = (props) => {
  const dispatch = useDispatch()
  const customerState = useSelector(state => state.order?.customer || {})
  const { detail: customerDetailObj } = customerState || {}

  const enhancedProps = {
    dispatch,
    customerDetailObj,
    ...props
  }

  return <CustomerDetailContent {...enhancedProps} />
}

export default CustomerDetailContentWrapper

